import type { PublicClassStatusFS } from './types';

export const STATUS_LABEL: Record<PublicClassStatusFS, string> = {
  scheduled: 'Scheduled',
  in_progress: 'Live',
  finished: 'Final',
};

/**
 * The projection's dates are true UTC instants (the sync converts the show's
 * wall-clock times through its timezone), so they must be read back in the
 * show's zone. Formatting in the viewer's zone would slide a 9am class by however
 * far their browser is from the venue, and formatting in UTC would put an
 * evening class in Florida on the next day.
 *
 * Returns `undefined` for a blank or unknown zone: Intl throws on a bad
 * `timeZone`, and one bad value from the database must not blank the widget.
 */
export function safeZone(timezone: string | null | undefined): string | undefined {
  const zone = (timezone ?? '').trim();
  if (!zone) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return undefined;
  }
}

function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A document written before the projection carried a timezone falls back to UTC,
 * which is how this widget formatted every date until then.
 */
export function formatClassDate(iso: string | null, timezone?: string | null): string {
  const date = parseInstant(iso);
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    timeZone: safeZone(timezone) ?? 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** `9:00 AM` at the venue. Blank when the class has no start time of its own. */
export function formatStartTime(iso: string | null, timezone: string | null | undefined): string {
  const date = parseInstant(iso);
  if (!date) return '';
  return date.toLocaleTimeString(undefined, {
    timeZone: safeZone(timezone) ?? 'UTC',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The calendar day an instant falls on at the venue, as `YYYY-MM-DD`.
 *
 * `en-CA` formats as `YYYY-MM-DD` natively; the parts are still reassembled by
 * type so a browser that orders them differently cannot break the comparison.
 */
export function dayKey(instant: Date, timezone: string | null | undefined): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: safeZone(timezone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** As above, for a stored instant. Blank when there is none. */
export function classDayKey(iso: string | null, timezone: string | null | undefined): string {
  const date = parseInstant(iso);
  return date ? dayKey(date, timezone) : '';
}

/**
 * `?date=` is a fixed `YYYY-MM-DD` or `today`. Anything else is rejected rather
 * than guessed at: in an embed nobody is watching, the wrong day's classes read
 * as fact, where an error is at least visibly an error.
 */
export function isValidDateParam(raw: string): boolean {
  if (raw.toLowerCase() === 'today') return true;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return false;
  const [, year, month, day] = match.map(Number) as [number, number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  // Rejects 2026-02-30, which Date would otherwise roll into March.
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/**
 * `2026-02-14` → `Sat, Feb 14`, or `Saturday, February 14` when `long`. The key is
 * a calendar day, so it is formatted in UTC.
 */
export function formatDayKey(key: string, style: 'short' | 'long' = 'short'): string {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    weekday: style,
    month: style,
    day: 'numeric',
  });
}

/**
 * The class name, with the height in front only when the name does not already
 * have it. Most names do (`$35,000 Welcome CSI3* 1.45m`), and the height twice
 * reads as a mistake. `0.85` also matches a name with `.85m`.
 */
export function classTitle(heightLabel: string, className: string): string {
  const height = heightLabel.trim();
  if (!height) return className;
  const short = height.replace(/^0(?=\.)/, '');
  return className.includes(short) ? className : `${height} ${className}`;
}
